import DSSecondaryButton from '../design-system/DSSecondaryButton';
import DSCircularProgress from '../design-system/DSCircularProgress';
import DSTooltip from '../design-system/DSTooltip';
import { ReactComponent as SendIcon } from '../design-system/Icons/SendIcon.svg';
import PreviewUnavailableModal, {
  PreviewUnavailableReason,
} from '../views/Klydos/components/PreviewUnavailableModal';
import ConnectClockModal from '../views/Klydos/components/ConnectClockModal';
import { SendableContentKind } from '../hooks/useSendToClock';

type SendToClockButtonProps = {
  /** False when there is nothing to send — the button is not rendered at all. */
  canSend: boolean;
  clockName?: string;
  kind: SendableContentKind;
  isOnClock: boolean;
  isDisabled: boolean;
  isSending: boolean;
  unavailableReason: PreviewUnavailableReason | null;
  closeUnavailable: () => void;
  onClick: () => void;
  onUpdateClock: () => void;
  isUpdating: boolean;
  isConnectModalOpen: boolean;
  closeConnectModal: () => void;
  onConnectClock: (serialNumber: string) => void;
  isConnecting: boolean;
};

/**
 * "Send to Clock" on the klydo and collection pages. Takes `useSendToClock`'s
 * return value as-is, so call sites are `<SendToClockButton {...sendToClock} />`.
 *
 * A collection already on the clock shows a disabled "Sent" — that state is read
 * live and clears itself. A klydo shows "Send again" and stays clickable, since
 * its marker is per-browser and the API re-queues happily.
 *
 * With no clock connected the button still shows and opens the connect modal,
 * then resumes the send — same entry point as the draft page's "Preview On ...".
 */
const SendToClockButton = ({
  canSend,
  clockName,
  kind,
  isOnClock,
  isDisabled,
  isSending,
  unavailableReason,
  closeUnavailable,
  onClick,
  onUpdateClock,
  isUpdating,
  isConnectModalOpen,
  closeConnectModal,
  onConnectClock,
  isConnecting,
}: SendToClockButtonProps) => {
  if (!canSend) return null;

  const clockLabel = clockName || 'your Klydoclock';
  const isCollection = kind === SendableContentKind.Collection;

  const label = isOnClock
    ? isCollection
      ? 'Sent ✓'
      : 'Send again'
    : 'Send to Clock';

  const tooltip =
    isOnClock && isCollection
      ? `Already on ${clockLabel}`
      : `Send to ${clockLabel}`;

  return (
    <>
      <DSTooltip title={tooltip} placement="bottom">
        {/* A disabled MUI button fires no pointer events, so the tooltip needs a
            wrapper it can attach to — otherwise the one state that explains the
            greyed-out button is the state that cannot show it. */}
        <span style={{ display: 'inline-flex' }}>
          {/* Same shape as "Preview On ..." on the draft klydo page, with a send
              icon instead of its eye — this button dispatches rather than views. */}
          <DSSecondaryButton
            variant="contained"
            size="medium"
            startIcon={
              isSending ? <DSCircularProgress size={16} /> : <SendIcon />
            }
            onClick={onClick}
            disabled={isDisabled}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {label}
          </DSSecondaryButton>
        </span>
      </DSTooltip>

      <ConnectClockModal
        open={isConnectModalOpen}
        onClose={closeConnectModal}
        onConnect={onConnectClock}
        isLoading={isConnecting}
      />

      {unavailableReason && (
        <PreviewUnavailableModal
          open
          onClose={closeUnavailable}
          reason={unavailableReason}
          onUpdate={onUpdateClock}
          isUpdating={isUpdating}
          title="Send Unavailable"
          message={`Update your KlydoClock to send this ${
            isCollection ? 'collection' : 'Klydo'
          }.`}
        />
      )}
    </>
  );
};

export default SendToClockButton;
